import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

/* Shared button styles */
const sharedStyles = css`
  display: inline-block;
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  border-radius: ${({ theme }) => theme.radii.medium};
  cursor: pointer;
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  transition: background-color ${({ theme }) => theme.transitions.fast}, transform ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.surface};
  text-align: center;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

/* Primary Variant */
const PrimaryBase = styled.button`
  ${sharedStyles}
  background-color: ${({ theme }) => theme.colors.primary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* Secondary Variant */
const SecondaryBase = styled(PrimaryBase)`
  background-color: ${({ theme }) => theme.colors.secondary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* Destructive Variant */
const DestructiveBase = styled(PrimaryBase)`
  background-color: ${({ theme }) => theme.colors.error};

  &:hover {
    background-color: ${({ theme }) => theme.colors.warning};
  }
`;

/* Pink Variant */
const PinkBase = styled(PrimaryBase)`
  background-color: ${({ theme }) => theme.colors.pink || "#E07856"};

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* Link Variants */
const PrimaryLink = styled(Link)`
  ${sharedStyles}
  background-color: ${({ theme }) => theme.colors.primary};
`;

const SecondaryLink = styled(Link)`
  ${sharedStyles}
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const DestructiveLink = styled(Link)`
  ${sharedStyles}
  background-color: ${({ theme }) => theme.colors.error};
`;

/* Pink Link Variant */
const PinkLink = styled(Link)`
  ${sharedStyles}
  background-color: ${({ theme }) => theme.colors.pink || "#E07856"};
`;

/* Component Export */
export const Button = ({ to, variant = "primary", ...props }) => {
  const variants = {
    primary: { Button: PrimaryBase, Link: PrimaryLink },
    secondary: { Button: SecondaryBase, Link: SecondaryLink },
    destructive: { Button: DestructiveBase, Link: DestructiveLink },
    pink: { Button: PinkBase, Link: PinkLink },
  };

  const VariantSet = variants[variant] || variants.primary;

  if (to) {
    const VariantLink = VariantSet.Link;
    return <VariantLink to={to} {...props} />;
  }

  const VariantButton = VariantSet.Button;
  return <VariantButton {...props} />;
};


// Named exports for backward compatibility
export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;

export default Button;
